<!--
  Partner — dettaglio:
  colonna principale (Sedi → Tariffe & Valet → Dipendenti) + riepilogo sticky
  a destra (Riepilogo, Pagamenti Nexi, Accesso API).

  Cose non ovvie, che dipendono da come è fatto il backend:

  1) ANAGRAFICA IN SOLA LETTURA PER CHI NON È SUPERADMIN.
     Su `PATCH /partner/:id` il backend, se l'utente ha un ruolo tenant, tiene
     solo le colonne `partner_*` e **scarta il resto in silenzio** (nessun 403).
     Lasciare i campi editabili farebbe vedere "salvato" senza che cambi nulla.

  2) DUE CHIAVI DIVERSE.
     - `API_key` + `domain` + `alias` = credenziali di pagamento Nexi, in chiaro
       ma `@Exclude()`: si leggono solo da `GET /partner/info-payment/:id`.
     - Accesso API = chiave `sk_simba_…` generata dal server, che ne salva
       solo l'hash: il testo in chiaro esiste una volta sola, alla generazione.

  3) DUE TABELLE DI FASCE, DIVERSE.
     - `tariff_band` → tariffe dei servizi (pickup, fleet/journey, transfer…),
       con `operators` per distinguere 1 e 2 operatori.
     - `valet_km_band` → listino del pacchetto valet: per ogni fascia km il
       prezzo Simba di UN pickup a scaglioni di volume, uno/due/tre o più
       (più quello del venditore, che imposta il partner). I km
       oltre quelli del pacchetto si pagano al €/km della fascia
       (`excess_price_per_km`, e quello del venditore), tratta per tratta.
       Non ha date di validità: si modifica sul posto.
     Per entrambe il partner usa le SUE fasce se ne ha, altrimenti quelle
     globali (partner NULL): da qui non si toccano mai quelle globali.

  4) LE FASCE VALGONO SOLO CON `TARIFF_ENGINE=bands`.
     Senza, il prezzo esce dalle 64 colonne legacy del partner: modificando
     una fascia i preventivi non cambiano.
-->
<template>
  <div
    v-loading="loading"
    class="pdt"
  >
    <!-- ============================= INTESTAZIONE ============================ -->
    <div class="pdt__head">
      <button
        type="button"
        class="pdt__back"
        @click="goBack"
      >
        ‹
      </button>
      <div class="pdt__htitle">
        <h1>
          {{ partner.company_name || 'Partner' }}
          <span
            class="tpill"
            :class="isNotturno ? 'tpill--nott' : 'tpill--auto'"
          >{{ isNotturno ? 'Notturno' : 'Automotive' }}</span>
        </h1>
        <div class="pdt__idmono">
          {{ partner.piva ? `P.IVA ${partner.piva}` : 'P.IVA non indicata' }}
        </div>
      </div>
      <div class="pdt__sp" />
      <div class="pdt__acts">
        <el-button
          v-if="canEdit && !editing"
          size="small"
          @click="startEdit"
        >
          ✎ Modifica
        </el-button>
        <template v-if="editing">
          <el-button
            size="small"
            @click="cancelEdit"
          >
            Annulla
          </el-button>
          <el-button
            size="small"
            type="primary"
            :loading="saving"
            @click="save"
          >
            Salva
          </el-button>
        </template>
      </div>
    </div>

    <div
      v-if="!canEdit"
      class="warnbox warnbox--info"
    >
      <span class="warnbox__ico">ⓘ</span>
      <div class="warnbox__txt">
        <b>Sola lettura.</b>
        La modifica di anagrafica, tariffe e credenziali è riservata a Simba
        (superadmin).
      </div>
    </div>

    <div class="pdt__grid">
      <!-- =============================== COLONNA =========================== -->
      <div class="pdt__col">
        <!-- ------------------------------ SEDI ---------------------------- -->
        <section class="card">
          <div class="card__hd">
            <h3>Sedi</h3>
            <span class="pdt__sp" />
            <el-button
              v-if="canEdit"
              size="small"
              @click="addHeadquarter"
            >
              ＋ Aggiungi sede
            </el-button>
          </div>
          <!-- Altezza fissa: un partner con molte sedi allungherebbe la
               pagina all'infinito, spingendo tariffe e dipendenti fuori vista. -->
          <div class="card__bd card__bd--scroll">
            <div
              v-if="!headquarters.length"
              class="pdt__empty"
            >
              Nessuna sede registrata.
            </div>
            <div
              v-for="h in headquarters"
              :key="h.id"
              class="sede"
              :class="{ 'sede--ro': !canEdit }"
              @click="openHeadquarter(h)"
            >
              <div class="sede__st">
                {{ h.name || 'Sede' }}
              </div>
              <div class="sede__addr">
                {{ addressOf(h) }}
              </div>
              <!-- Se mancano le coordinate la sede non entra nei calcoli di
                   distanza: va segnalato, non nascosto. -->
              <div
                class="sede__geo"
                :class="{ 'is-missing': !hasCoords(h) }"
              >
                {{ hasCoords(h)
                  ? `📍 ${h.latitude}, ${h.longitude}`
                  : '⚠ posizione non geolocalizzata' }}
              </div>
              <!-- COPERTURA DRIVER. È il dato che decide se questa sede ha
                   orari prenotabili: il planner cerca i driver per PROVINCIA
                   della sede, non per partner (né per le vecchie zone `area`,
                   in dismissione). Provincia non impostata o senza driver =
                   nessuno slot, a qualsiasi ora — e senza scriverlo qui la
                   causa è invisibile. -->
              <div
                class="sede__cov"
                :class="{ 'is-missing': !h.provinceId || driverCount(h) === 0 }"
              >
                <template v-if="!h.provinceId">
                  ⚠ nessuna provincia assegnata: questa sede non produce orari
                  disponibili
                </template>
                <template v-else-if="driverCount(h) === null">
                  Provincia <b>{{ provinceLabel(h) }}</b> · driver in corso di conteggio…
                </template>
                <template v-else-if="driverCount(h) === 0">
                  ⚠ Provincia <b>{{ provinceLabel(h) }}</b>: nessun driver operativo,
                  nessun orario prenotabile
                </template>
                <template v-else>
                  Provincia <b>{{ provinceLabel(h) }}</b> ·
                  <b>{{ driverCount(h) }}</b>
                  {{ driverCount(h) === 1 ? 'driver operativo' : 'driver operativi' }}
                </template>
              </div>
              <div class="sede__meta">
                <span>Città <b>{{ h.city || '—' }}</b></span>
                <span>Provincia <b>{{ h.province || '—' }}</b></span>
                <span>Abbonamento <b>{{ euro(h.abbonamento) }}/mese</b></span>
              </div>
            </div>
          </div>
        </section>

        <!-- ------------------------ TARIFFE & VALET ----------------------- -->
        <section class="card">
          <div class="card__hd">
            <h3>Tariffe &amp; Valet</h3>
            <span class="pdt__sp" />
          </div>
          <div class="card__bd">
            <div
              v-if="!tariffGroups.length && !valetBands.length"
              class="pdt__empty"
            >
              Nessuna fascia definita per questo partner.
            </div>

            <div
              v-if="tariffGroups.length"
              class="listino"
            >
              <span class="listino__t">Tariffe Simba</span>
              <span class="listino__n">quello che fatturiamo al partner</span>
            </div>

            <!-- una sotto-scheda per servizio -->
            <div
              v-for="g in tariffGroups"
              :key="g.service"
              class="subcard"
            >
              <div class="subcard__sh">
                <span
                  class="chip"
                  :class="`chip--${g.tone}`"
                >{{ g.label }}</span>
                <span
                  v-if="g.twoOperators"
                  class="subcard__note"
                >a catena / singolo</span>
                <span
                  v-if="g.global"
                  class="subcard__note subcard__note--global"
                >listino globale Simba: il partner non ne ha uno suo</span>
                <span class="pdt__sp" />
                <el-button
                  v-if="canEdit && g.hasOwn"
                  size="small"
                  type="danger"
                  plain
                  @click="deleteListino(g)"
                >
                  Elimina listino
                </el-button>
                <el-button
                  v-if="canEdit"
                  size="small"
                  @click="openBandEditor(g)"
                >
                  {{ g.global ? 'Crea listino del partner' : 'Modifica fasce' }}
                </el-button>
              </div>

              <!-- fleet: gli operatori diventano colonne -->
              <table
                v-if="g.twoOperators"
                class="mtab"
              >
                <thead>
                  <tr>
                    <th>Fascia km</th>
                    <th class="num">
                      A catena
                    </th>
                    <th class="num">
                      Singolo
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="r in g.rows"
                    :key="r.key"
                  >
                    <td>{{ r.range }}</td>
                    <td class="num">
                      {{ r.op1 }}
                    </td>
                    <td class="num">
                      {{ r.op2 }}
                    </td>
                  </tr>
                </tbody>
              </table>

              <table
                v-else
                class="mtab"
              >
                <thead>
                  <tr>
                    <th>Fascia km</th>
                    <th class="num">
                      Tariffa
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="r in g.rows"
                    :key="r.key"
                  >
                    <td>{{ r.range }}</td>
                    <td class="num">
                      {{ r.price }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- A SIMBA MOBILITY servono i listini di tutti i servizi,
                 perché con i suoi si preventiva quello che i clienti chiedono
                 dall'app senza scegliere un partner. -->
            <p
              v-if="isSimbaMobility"
              class="pdt__hint"
            >
              Con questi listini l'app calcola i preventivi delle richieste
              <b>senza partner</b>: vale la pena tenerli completi, notturno
              compreso.
            </p>

            <!-- Servizi previsti dal tipo di partner ma ancora SENZA fasce.
                 Per il notturno è il caso classico del listino serale mai
                 compilato: senza queste righe le corse fra le 22 e le 6
                 quoterebbero 0 e non ci sarebbe modo di rimediare da qui. -->
            <div
              v-for="g in missingServices"
              :key="`miss-${g.service}-${g.immatricolato === false ? 'prova' : 'std'}`"
              class="subcard subcard--missing"
            >
              <div class="subcard__sh">
                <span
                  class="chip"
                  :class="`chip--${g.tone}`"
                >{{ g.label }}</span>
                <span class="subcard__note">nessuna fascia impostata</span>
                <span class="pdt__sp" />
                <el-button
                  v-if="canEdit"
                  size="small"
                  type="primary"
                  @click="openBandEditor(g)"
                >
                  Imposta fasce
                </el-button>
              </div>
              <p class="pdt__empty">
                <template v-if="g.ripiego">{{ g.ripiego }}</template>
                <template v-else>
                  Le corse di questo servizio quotano <b>0</b> finché il listino
                  non esiste.
                </template>
              </p>
            </div>

            <!-- pacchetto valet -->
            <div class="subcard">
              <div class="subcard__sh">
                <span class="subcard__title">Pacchetto valet</span>
                <span
                  v-if="valetIsGlobal"
                  class="subcard__note subcard__note--global"
                >listino globale Simba: il partner non ne ha uno suo</span>
                <span class="pdt__sp" />
                <span class="subcard__note">→ COD2</span>
                <el-button
                  v-if="canEdit"
                  size="small"
                  :type="effectiveValetBands.length ? 'default' : 'primary'"
                  @click="openValetEditor"
                >
                  {{ ownValetBands.length
                    ? 'Modifica fasce'
                    : (valetIsGlobal ? 'Crea listino del partner' : 'Imposta fasce') }}
                </el-button>
              </div>

              <table
                v-if="effectiveValetBands.length"
                class="mtab"
              >
                <thead>
                  <tr>
                    <th>Fascia km</th>
                    <th
                      v-for="n in PICKUP_OPTIONS"
                      :key="n"
                      class="num"
                    >
                      {{ n === 3 ? '3 o più pickup' : `${n} pickup` }}
                    </th>
                    <th class="num">
                      Km oltre il pacchetto
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="b in effectiveValetBands"
                    :key="b.id"
                  >
                    <td>{{ kmRange(b.from_km, b.to_km) }}</td>
                    <td
                      v-for="n in PICKUP_OPTIONS"
                      :key="n"
                      class="num"
                    >
                      <template v-if="b[`annual_price_${n}`] != null">
                        {{ euro(b[`annual_price_${n}`]) }}/anno
                        <span class="mtab__sub">{{ euro(monthlyOf(b[`annual_price_${n}`])) }}/mese</span>
                        <span
                          v-if="b[`partner_annual_price_${n}`] != null"
                          class="mtab__sub mtab__sub--share"
                        >venditore {{ euro(b[`partner_annual_price_${n}`]) }}/anno</span>
                      </template>
                      <span
                        v-else
                        class="pdt__dash"
                      >—</span>
                    </td>
                    <td class="num">
                      <template v-if="b.excess_price_per_km != null">
                        {{ euro(b.excess_price_per_km) }}/km
                        <span
                          v-if="b.partner_excess_price_per_km != null"
                          class="mtab__sub mtab__sub--share"
                        >venditore {{ euro(b.partner_excess_price_per_km) }}/km</span>
                      </template>
                      <span
                        v-else
                        class="pdt__dash"
                      >—</span>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div
                v-else
                class="pdt__empty"
              >
                Nessuna fascia valet definita: i pacchetti non hanno un listino da cui prendere il prezzo.
              </div>

              <div class="notebox">
                Prezzo <b>per pickup</b>, a scaglioni di volume: il pacchetto costa
                prezzo × numero di pickup, e i mesi decidono solo la scadenza.
                I km di ogni tratta oltre quelli
                compresi nel pacchetto si pagano al <b>prezzo al km della fascia</b>
                («Km oltre il pacchetto»); senza, non si addebitano.
              </div>
            </div>

            <!-- Secondo listino, impaginato come il primo: stesse fasce,
                 stessa tabella, ma sono i prezzi che il gestionale partner
                 mostra al proprio cliente. Tenuti separati dalle tariffe
                 Simba, altrimenti ogni fascia comparirebbe due volte. -->
            <template v-if="partnerGroups.length">
              <div class="listino">
                <span class="listino__t">Tariffe del partner</span>
                <span class="listino__n">ricarico · mostrate nel gestionale partner</span>
              </div>

              <div class="notebox notebox--plain">
                Il partner parte dalla tariffa Simba qui sopra e può alzarla:
                la differenza è il suo ricarico.
              </div>

              <div
                v-for="g in partnerGroups"
                :key="`share-${g.service}`"
                class="subcard subcard--share"
              >
                <div class="subcard__sh">
                  <span
                    class="chip"
                    :class="`chip--${g.tone}`"
                  >{{ g.label }}</span>
                  <span
                    v-if="g.twoOperators"
                    class="subcard__note"
                  >a catena / singolo</span>
                </div>

                <table
                  v-if="g.twoOperators"
                  class="mtab"
                >
                  <thead>
                    <tr>
                      <th>Fascia km</th>
                      <th class="num">
                        A catena
                      </th>
                      <th class="num">
                        Singolo
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="r in g.rows"
                      :key="r.key"
                    >
                      <td>{{ r.range }}</td>
                      <td class="num">
                        {{ r.op1 }}
                      </td>
                      <td class="num">
                        {{ r.op2 }}
                      </td>
                    </tr>
                  </tbody>
                </table>

                <table
                  v-else
                  class="mtab"
                >
                  <thead>
                    <tr>
                      <th>Fascia km</th>
                      <th class="num">
                        Tariffa
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="r in g.rows"
                      :key="r.key"
                    >
                      <td>{{ r.range }}</td>
                      <td class="num">
                        {{ r.price }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </template>
          </div>
        </section>

        <!-- --------------------------- DIPENDENTI ------------------------- -->
        <section class="card">
          <div class="card__hd">
            <h3>Dipendenti</h3>
            <span
              v-if="employees.length"
              class="emp__count"
            >{{ empFiltrati.length === employees.length ? employees.length : `${empFiltrati.length} di ${employees.length}` }}</span>
            <span class="pdt__sp" />
            <el-button
              size="small"
              @click="addEmployee"
            >
              ＋ Aggiungi dipendente
            </el-button>
          </div>
          <div class="card__bd">
            <div
              v-if="!employees.length"
              class="pdt__empty"
            >
              Nessun dipendente collegato a questo partner.
            </div>
            <!-- Con tanti dipendenti: si cerca per nome, mail o telefono, e
                 si filtra per ruolo e per sede. -->
            <div
              v-if="employees.length > 4"
              class="emp__filters"
            >
              <el-input
                v-model="empCerca"
                placeholder="Cerca per nome, mail o telefono"
                clearable
                size="small"
                class="emp__search"
              />
              <el-select
                v-model="empRuolo"
                placeholder="Tutti i ruoli"
                clearable
                size="small"
                class="emp__sel"
              >
                <el-option
                  v-for="r in ruoliPresenti"
                  :key="r.value"
                  :label="r.label"
                  :value="r.value"
                />
              </el-select>
              <el-select
                v-if="headquarters.length > 1"
                v-model="empSede"
                placeholder="Tutte le sedi"
                clearable
                filterable
                size="small"
                class="emp__sel"
              >
                <el-option
                  v-for="h in headquarters"
                  :key="h.id"
                  :label="h.name || h.city"
                  :value="h.id"
                />
              </el-select>
            </div>
            <div
              v-if="employees.length && !empFiltrati.length"
              class="pdt__empty"
            >
              Nessun dipendente con questi filtri.
            </div>
            <div
              v-for="e in empFiltrati"
              :key="e.id"
              class="emp"
              @click="openEmployee(e)"
            >
              <span class="ava">{{ empInitials(e) }}</span>
              <div>
                <div class="emp__nm">
                  {{ [e.name, e.surname].filter(Boolean).join(' ') || '—' }}
                </div>
                <div class="emp__sub">
                  {{ e.mail || '—' }}
                  <template v-if="e.telephone_number"> · {{ e.telephone_number }}</template>
                </div>
              </div>
              <div class="emp__right">
                <span class="emp__rl">{{ roleLabel(e.role) }}</span>
                <div class="emp__sedes">
                  <span
                    v-for="h in (e.headquarters || [])"
                    :key="h.id"
                    class="schip"
                  >{{ h.name || h.city }}</span>
                </div>
                <!-- La riga apre il dipendente: il click sul pulsante non
                     deve arrivarle sotto. -->
                <el-button
                  v-if="canEdit && e.mail"
                  class="emp__send"
                  size="small"
                  text
                  :loading="inviandoAccesso === e.id"
                  @click.stop="inviaAccesso(e)"
                >
                  Rinvia accesso
                </el-button>
                <el-button
                  v-if="canEdit"
                  class="emp__send"
                  size="small"
                  text
                  type="danger"
                  :loading="eliminandoDip === e.id"
                  @click.stop="eliminaDipendente(e)"
                >
                  Elimina
                </el-button>
              </div>
            </div>
          </div>
        </section>
      </div>

      <!-- ================================ ASIDE ============================ -->
      <div class="pdt__aside">
        <!-- ---------------------------- RIEPILOGO ------------------------- -->
        <section class="card">
          <div class="card__hd">
            <h3>Riepilogo</h3>
          </div>
          <div class="card__bd">
            <div class="kv">
              <div class="kv__r">
                <span class="kv__k">Tipo</span>
                <span class="kv__v">
                  <span
                    class="tpill"
                    :class="isNotturno ? 'tpill--nott' : 'tpill--auto'"
                  >{{ isNotturno ? 'Notturno' : 'Automotive' }}</span>
                </span>
              </div>
              <div class="kv__r">
                <span class="kv__k">Servizi</span>
                <span class="kv__v">
                  <!-- In modifica i servizi si aggiungono e si tolgono: un
                       partner nato col solo fleet può prendere anche il
                       pickup senza doverlo ricreare. Il transfer non ha un
                       suo interruttore, esiste quando ha un listino. -->
                  <span
                    v-if="editing"
                    class="svcwrap"
                  >
                    <el-checkbox
                      v-if="!isNotturno"
                      v-model="draft.booking_type"
                    >
                      Pickup &amp; Delivery
                    </el-checkbox>
                    <el-checkbox
                      v-if="!isNotturno"
                      v-model="draft.journey_type"
                    >
                      Spostamenti interni
                    </el-checkbox>
                    <span
                      v-if="isNotturno"
                      class="pdt__hint"
                    >Il notturno ha un servizio solo: non si cambia da qui.</span>
                  </span>
                  <span
                    v-else
                    class="svcwrap"
                  >
                    <span
                      v-for="s in activeServices"
                      :key="s.label"
                      class="chip"
                      :class="`chip--${s.tone}`"
                    >{{ s.label }}</span>
                    <span
                      v-if="!activeServices.length"
                      class="pdt__dash"
                    >—</span>
                  </span>
                </span>
              </div>
              <div class="kv__r">
                <span class="kv__k">Advisor</span>
                <span class="kv__v">{{ advisorNames || '—' }}</span>
              </div>
              <div class="kv__r">
                <span class="kv__k">Costi aggiuntivi</span>
                <span class="kv__v">
                  <span :class="partner.has_extra_costs ? 'flag-on' : 'flag-off'">
                    {{ partner.has_extra_costs ? 'Attivi' : 'Non attivi' }}
                  </span>
                </span>
              </div>
              <div class="kv__r">
                <span class="kv__k">Provvigione</span>
                <span class="kv__v">
                  {{ partner.provvigione != null ? partner.provvigione + '%' : '—' }}
                </span>
              </div>
              <div class="kv__r">
                <span class="kv__k">Email</span>
                <span class="kv__v">
                  <el-input
                    v-if="editing"
                    v-model="draft.mail"
                    size="small"
                  />
                  <template v-else>{{ partner.mail || '—' }}</template>
                </span>
              </div>
              <div class="kv__r">
                <span class="kv__k">Telefono</span>
                <span class="kv__v">
                  <el-input
                    v-if="editing"
                    v-model="draft.phone_number"
                    size="small"
                  />
                  <template v-else>{{ partner.phone_number || '—' }}</template>
                </span>
              </div>
              <div class="kv__r">
                <span class="kv__k">Sedi</span>
                <span class="kv__v">{{ sediSummary }}</span>
              </div>
            </div>
          </div>
        </section>

        <!-- -------------------------- REPORT MENSILE ---------------------- -->
        <!-- Un Excel con tutte le corse del mese: spostamenti interni e gambe
             delle prenotazioni, con km e minuti di driver e jolly (vedi
             services/partnerReport.js). -->
        <section class="card">
          <div class="card__hd">
            <h3>Report mensile</h3>
          </div>
          <div class="card__bd">
            <div class="report">
              <el-date-picker
                v-model="reportMonth"
                type="month"
                format="MM/YYYY"
                value-format="YYYY-MM"
                :clearable="false"
                size="small"
                class="report__month"
                aria-label="Mese del report"
              />
              <el-button
                type="primary"
                size="small"
                :loading="reportBusy"
                @click="downloadReport"
              >
                Scarica Excel
              </el-button>
            </div>
            <p class="report__hint">
              Una riga per corsa: spostamenti interni, pickup, transfer e
              notturno del mese, con lo stato (anche eliminate e annullate) e
              km e minuti di driver e jolly.
            </p>
          </div>
        </section>

        <!-- -------------------------- PAGAMENTI NEXI ---------------------- -->
        <section class="card">
          <div class="card__hd">
            <h3>Pagamenti — Nexi</h3>
          </div>
          <div class="card__bd">
            <div class="kv">
              <div class="kv__r">
                <span class="kv__k">Dominio</span>
                <span class="kv__v kv__v--mono">
                  <el-input
                    v-if="editing"
                    v-model="draft.domain"
                    size="small"
                    placeholder="https://ecommerce.nexi.it"
                  />
                  <template v-else>{{ payment.domain || '—' }}</template>
                </span>
              </div>
              <div class="kv__r">
                <span class="kv__k">Alias</span>
                <span class="kv__v kv__v--mono">
                  <el-input
                    v-if="editing"
                    v-model="draft.alias"
                    size="small"
                  />
                  <template v-else>{{ payment.alias || '—' }}</template>
                </span>
              </div>
              <div class="kv__r">
                <span class="kv__k">API key</span>
                <span class="kv__v kv__v--mono">
                  <el-input
                    v-if="editing"
                    v-model="draft.API_key"
                    size="small"
                    placeholder="Chiave fornita da Nexi"
                  />
                  <template v-else>{{ maskedNexiKey }}</template>
                </span>
              </div>
            </div>
          </div>
        </section>

        <!-- --------------------------- ACCESSO API ------------------------ -->
        <section class="card">
          <div class="card__hd">
            <h3>Accesso API</h3>
          </div>
          <div class="card__bd">
            <div class="pdt__apiRow">
              <StatusPill
                :kind="apiAccess.enabled ? 'ok' : 'muted'"
                :label="apiAccess.enabled ? 'Attiva' : 'Non attiva'"
              />
              <span
                v-if="apiAccess.created_at"
                class="pdt__apiMeta"
              >dal {{ fmtDate(apiAccess.created_at) }}</span>
            </div>
            <div
              v-if="apiAccess.enabled"
              class="pdt__apiMeta"
            >
              Ultimo utilizzo:
              {{ apiAccess.last_used_at ? fmtDate(apiAccess.last_used_at) : 'mai' }}
            </div>

            <!-- Il plaintext esiste solo ora: dopo resta il solo hash a DB. -->
            <div
              v-if="freshKey"
              class="warnbox pdt__freshKey"
            >
              <span class="warnbox__ico">⚠</span>
              <div class="warnbox__txt">
                <b>Copiala adesso: non sarà più visibile.</b>
                <code class="pdt__keyValue">{{ freshKey }}</code>
                <el-button
                  size="small"
                  @click="copyKey"
                >
                  Copia
                </el-button>
              </div>
            </div>

            <div
              v-if="canEdit"
              class="pdt__apiActions"
            >
              <el-button
                size="small"
                :loading="apiBusy"
                @click="issueKey"
              >
                {{ apiAccess.enabled ? 'Rigenera' : 'Genera chiave' }}
              </el-button>
              <el-button
                v-if="apiAccess.enabled"
                size="small"
                type="danger"
                plain
                :loading="apiBusy"
                @click="revokeKey"
              >
                Revoca
              </el-button>
            </div>
          </div>
        </section>
      </div>
    </div>

    <!-- Editor delle fasce: chiude quelle in corso e ne crea di nuove. -->
    <el-dialog
      v-model="bandDialog"
      :title="`Fasce · ${bandLabel}`"
      :width="bandTwoOp ? 'min(1060px, 96vw)' : 'min(820px, 94vw)'"
    >
      <!-- Il modo di calcolo si sceglie per ogni fascia: il
           backend applica a ogni corsa quello della fascia in cui cade. -->
      <BandTable
        v-model="bandRows"
        :default-mode="bandDefaultMode"
        :two-operators="bandTwoOp"
        :with-share="true"
      />
      <p class="pdt__dlgNote">
        Salvando, le fasce attualmente in vigore per questo servizio vengono
        <b>chiuse a ieri</b> e queste diventano valide da oggi. Le corse già
        confermate non cambiano prezzo.
      </p>
      <template #footer>
        <el-button @click="bandDialog = false">
          Annulla
        </el-button>
        <el-button
          type="primary"
          :loading="savingBands"
          @click="saveBands"
        >
          Salva listino
        </el-button>
      </template>
    </el-dialog>

    <!-- Editor delle fasce VALET. A differenza di `tariff_band` qui non ci sono
         date di validità: le fasce si modificano sul posto. Lo storico non
         cambia perché ogni addebito salva il suo importo sulla prenotazione
         (`booking.valet_charged_amount`). -->
    <el-dialog
      v-model="valetDialog"
      title="Fasce · Pacchetto valet"
      width="min(860px, 96vw)"
    >
      <ValetBandTable
        v-model="valetRows"
        :show-partner="true"
      />
      <p
        v-if="valetError"
        class="pdt__dlgErr"
        role="alert"
      >
        {{ valetError }}
      </p>
      <p class="pdt__dlgNote">
        Prezzi Simba di UN pickup, a scaglioni di volume (uno, due, tre o
        più). I prezzi del venditore li imposta il partner dal suo
        gestionale e qui si vedono soltanto. I pacchetti già venduti
        conservano il loro prezzo. «Km oltre il pacchetto» è il prezzo al km della fascia:
        una corsa col valet paga solo i km di ogni tratta oltre quelli
        compresi.
        <template v-if="valetIsGlobal">
          Il partner oggi usa il listino globale: salvando nasce il suo, e
          quello globale non cambia.
        </template>
        Una fascia già usata da un consumo non si può togliere, solo cambiarne
        i prezzi.
      </p>
      <template #footer>
        <el-button @click="valetDialog = false">
          Annulla
        </el-button>
        <el-button
          type="primary"
          :loading="savingValet"
          @click="saveValetBands"
        >
          Salva fasce valet
        </el-button>
      </template>
    </el-dialog>

    <!-- ------------------------- nuovo dipendente -------------------------- -->
    <el-dialog
      v-model="empDialog"
      title="Nuovo dipendente"
      width="560px"
    >
      <div class="pdt__form">
        <div class="pdt__grid2">
          <div class="pdt__fld">
            <label>Nome</label>
            <el-input v-model="empDraft.name" />
          </div>
          <div class="pdt__fld">
            <label>Cognome</label>
            <el-input v-model="empDraft.surname" />
          </div>
        </div>
        <div class="pdt__fld">
          <label>Email</label>
          <el-input
            v-model="empDraft.mail"
            placeholder="nome.cognome@partner.it"
          />
        </div>
        <div class="pdt__grid2">
          <div class="pdt__fld">
            <label>Telefono <span class="pdt__opt">(facoltativo)</span></label>
            <el-input v-model="empDraft.telephone_number" />
          </div>
          <div class="pdt__fld">
            <label>Ruolo</label>
            <el-select
              v-model="empDraft.role"
              style="width: 100%"
            >
              <el-option
                v-for="r in EMP_ROLES"
                :key="r.value"
                :label="r.label"
                :value="r.value"
              />
            </el-select>
          </div>
        </div>
        <div class="pdt__fld">
          <label>Sedi</label>
          <el-select
            v-model="empDraft.headquarterIds"
            multiple
            placeholder="Tutte le sedi del partner"
            style="width: 100%"
          >
            <el-option
              v-for="h in headquarters"
              :key="h.id"
              :label="h.name || h.city || 'Sede'"
              :value="h.id"
            />
          </el-select>
          <p
            v-if="!empDraft.headquarterIds.length"
            class="pdt__dlgNote"
          >
            ⚠ Senza almeno una sede il dipendente non comparirà in questo
            elenco: le persone si leggono passando dalle sedi del partner.
          </p>
        </div>
        <p class="pdt__dlgNote">
          La password viene generata dal server e inviata per email
          all'indirizzo indicato.
        </p>
      </div>
      <template #footer>
        <el-button @click="empDialog = false">
          Annulla
        </el-button>
        <el-button
          type="primary"
          :loading="savingEmp"
          :disabled="!canSaveEmp"
          @click="saveEmployee"
        >
          Crea dipendente
        </el-button>
      </template>
    </el-dialog>

    <!-- ------------------------ nuova / modifica sede ----------------------- -->
    <HeadquarterDialog
      v-model="hqDialog"
      :partner-id="partner.id || ''"
      :headquarter="hqEditing"
      @saved="load"
    />

    <!-- ----------------------- credenziali rinviate ----------------------- -->
    <el-dialog
      :model-value="!!credenziali"
      title="Accesso rinviato"
      width="440px"
      @update:model-value="(v) => { if (!v) credenziali = null; }"
    >
      <template v-if="credenziali">
        <p class="cred__txt">
          A <b>{{ credenziali.mail }}</b> è partita la mail di benvenuto con queste
          credenziali. Se non gli arriva, comunicagliele tu.
        </p>
        <div class="cred__box">
          <span class="cred__lbl">Utente</span>
          <code class="cred__val">{{ credenziali.mail }}</code>
          <span class="cred__lbl">Password</span>
          <code class="cred__val">{{ credenziali.password }}</code>
        </div>
      </template>
      <template #footer>
        <el-button @click="copiaCredenziali">
          Copia
        </el-button>
        <el-button
          type="primary"
          @click="credenziali = null"
        >
          Fatto
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ElMessage, ElMessageBox } from 'element-plus';
import partnerAPI from '../../services/partnerAPI.js';
import driverAPI from '../../services/driverAPI.js';
import authAPI from '../../services/authAPI.js';
import StatusPill from '../../components/ui/StatusPill.vue';
import BandTable from '../../components/ui/BandTable.vue';
import ValetBandTable from '../../components/ui/ValetBandTable.vue';
import HeadquarterDialog from '../../components/HeadquarterDialog.vue';
import {
  amountOr, bandPricing, bandRowsProblem, defaultModeFor, isExcessMode, unitFor, unitForMode,
} from '../../services/tariffModel.js';
import { isLoggedStore } from '../../store/logged.js';
import { defaultBandsFor } from '../../services/tariffDefaults.js';
import { loadPartnerMonthRows, buildPartnerReportXlsx } from '../../services/partnerReport.js';

// I ruoli a UI mappano sull'enum di user.abstract.ts.
const ROLE_LABELS = {
  admin: 'Amministratore',
  adminEmployee: 'Amministratore',
  employee: 'Dipendente',
  salesvendor: 'Venditore',
};

// Etichetta + tinta per servizio: stessa mappa UI↔DB del resto del gestionale.
const SERVICES = {
  journey: { label: 'Spostamento interno', tone: 'fleet' },
  // La consegna auto è deprecata e ha fasce proprie (`delivery`): questa
  // etichetta copre il solo servizio `pickup`.
  pickup: { label: 'Pickup', tone: 'pickup' },
  pickup_loaner: { label: 'Pickup · auto cortesia', tone: 'pickup' },
  delivery: { label: 'Consegna auto', tone: 'pickup' },
  escort: { label: 'Transfer', tone: 'acc' },
  escort_loaner: { label: 'Transfer · auto cortesia', tone: 'acc' },
  restaurant: { label: 'Notturno', tone: 'nott' },
  restaurant_night: { label: 'Notturno · serale', tone: 'nott' },
};

/**
 * Fra due fasce aperte sullo stesso scaglione, quale applica il backend.
 * Stesso ordine di `resolveBand`: prima quella del partner, poi la `valid_from`
 * più recente, poi la creata per ultima.
 *
 * ⚠️ Serve davvero: un salvataggio andato a metà lascia due fasce aperte sullo
 * stesso scaglione, e senza questo criterio la scheda ne mostrava una a caso —
 * quindi un prezzo che il backend non avrebbe mai applicato.
 */
function vinceSu(b, altra) {
  if (!!b.partner !== !!altra.partner) return !!b.partner;
  const da = String(b.valid_from || '');
  const daAltra = String(altra.valid_from || '');
  if (da !== daAltra) return da > daAltra;
  return String(b.created_at || '') > String(altra.created_at || '');
}

/**
 * Come `Promise.allSettled`, ma a blocchi: non più di `size` richieste in volo.
 * Il browser ne apre comunque sei per host e ognuna si porta dietro il
 * preflight CORS, quindi lanciarne cinquanta insieme non le fa arrivare prima
 * — le mette in coda, e verso un server remoto l'attesa non finiva più.
 */
async function aBlocchi(items, size, fn) {
  const out = [];
  for (let i = 0; i < items.length; i += size) {
    out.push(...(await Promise.allSettled(items.slice(i, i + size).map(fn))));
  }
  return out;
}

export default {
  name: 'DetailPartnerPage',
  components: { StatusPill, BandTable, ValetBandTable, HeadquarterDialog },

  setup() {
    const route = useRoute();
    const router = useRouter();
    const store = isLoggedStore();

    const partner = ref({});

    // ---- report mensile -----------------------------------------------------
    // Parte dal mese scorso: il report si scarica a mese chiuso.
    const meseScorso = () => {
      const d = new Date();
      d.setDate(1);
      d.setMonth(d.getMonth() - 1);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    };
    const reportMonth = ref(meseScorso());
    const reportBusy = ref(false);
    const downloadReport = async () => {
      const [year, month] = String(reportMonth.value).split('-').map(Number);
      if (!year || !month) return;
      reportBusy.value = true;
      try {
        const { rows, truncated } = await loadPartnerMonthRows(route.params.id, year, month);
        if (truncated) {
          ElMessage.error('Troppe corse in un mese per scaricarle tutte: il report sarebbe incompleto.');
          return;
        }
        if (!rows.length) {
          ElMessage.info('Nessuna corsa in quel mese.');
          return;
        }
        const { blob, filename } = await buildPartnerReportXlsx(rows, {
          partnerName: partner.value.company_name, year, month,
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        ElMessage.success(`Report scaricato: ${rows.length} ${rows.length === 1 ? 'corsa' : 'corse'}.`);
      } catch (e) {
        console.error('Report non generato:', e);
        ElMessage.error('Report non generato. Riprova.');
      } finally {
        reportBusy.value = false;
      }
    };
    const payment = ref({});
    const apiAccess = ref({ enabled: false });
    const headquarters = ref([]);
    const employees = ref([]);
    // Ricerca e filtri della lista dipendenti.
    const empCerca = ref('');
    const empRuolo = ref('');
    const empSede = ref('');
    // Amministratore e amministratore dipendente sono la stessa voce a UI.
    const ruoloUi = (r) => (r === 'adminEmployee' ? 'admin' : r);
    const ruoliPresenti = computed(() => {
      const visti = [...new Set(employees.value.map((e) => ruoloUi(e.role)).filter(Boolean))];
      return visti.map((value) => ({ value, label: ROLE_LABELS[value] || value }))
        .sort((a, b) => a.label.localeCompare(b.label));
    });
    const empFiltrati = computed(() => {
      const q = empCerca.value.trim().toLowerCase();
      return employees.value.filter((e) => {
        if (empRuolo.value && ruoloUi(e.role) !== empRuolo.value) return false;
        if (empSede.value && !(e.headquarters || []).some((h) => h.id === empSede.value)) return false;
        if (!q) return true;
        return [e.name, e.surname, `${e.name || ''} ${e.surname || ''}`, e.mail, e.telephone_number]
          .some((v) => String(v || '').toLowerCase().includes(q));
      });
    });
    const bands = ref([]);
    const valetBands = ref([]);
    const advisors = ref([]);

    const loading = ref(false);
    const editing = ref(false);
    const saving = ref(false);
    const apiBusy = ref(false);
    const freshKey = ref('');

    const draft = reactive({});

    const canEdit = computed(() => partnerAPI.canEditAnagrafica(store.role));
    const isNotturno = computed(() => partnerAPI.isNotturno(partner.value));

    const euro = (n) =>
      n == null || n === '' ? '—' : `€ ${Number(n).toFixed(2).replace('.', ',')}`;

    const kmRange = (from, to) =>
      to == null ? `oltre ${from} km` : `${from}–${to} km`;

    // La chiave Nexi è in chiaro a DB: si mostra mascherata, per non lasciarla
    // leggibile a chiunque passi davanti allo schermo.
    const maskedNexiKey = computed(() => {
      const k = payment.value.API_key;
      if (!k) return '—';
      return k.length <= 6 ? '••••' : `•••• •••• ${k.slice(-4)}`;
    });

    const advisorNames = computed(() =>
      advisors.value
        .map((a) => [a.name, a.surname].filter(Boolean).join(' '))
        .join(', ')
    );

    // I flag del partner sono ciò che determina la fatturazione (COD3).
    const activeServices = computed(() => {
      const out = [];
      if (partner.value.booking_type) {
        out.push(
          isNotturno.value
            ? { label: 'Notturno', tone: 'nott' }
            : { label: 'Pickup & Delivery', tone: 'pickup' }
        );
      }
      if (partner.value.journey_type) {
        out.push({ label: 'Spostamento interno', tone: 'fleet' });
      }
      return out;
    });

    const sediSummary = computed(() => {
      const n = headquarters.value.length;
      if (!n) return '—';
      const cities = [...new Set(headquarters.value.map((h) => h.city).filter(Boolean))];
      return cities.length ? `${n} · ${cities.join(', ')}` : String(n);
    });

    // Fascia DI QUESTO PARTNER (e non del listino globale, partner NULL).
    const ownBand = (b) => (b.partner ? b.partner.id : b.partnerId) === route.params.id;

    // Variante della fascia: `prova` sono le tariffe dei veicoli NON
    // immatricolati (si spostano con la targa prova), `normale` tutte le
    // altre. A DB la colonna è `immatricolato`: false = fascia dedicata,
    // NULL = vale in ogni caso.
    const variante = (b) => (b.immatricolato === false ? 'prova' : 'normale');
    const ETICHETTA_PROVA = ' · targa prova';

    // Fasce effettivamente in vigore oggi, senza doppioni.
    //
    // Si chiedono al server sia le fasce del partner sia quelle globali, ma NON
    // valgono insieme: `resolveBand` cerca prima quella del partner e usa la
    // globale solo se non la trova. Qui si replica la stessa precedenza, sulla
    // chiave con cui il backend risolve: servizio + listino + operatori + range.
    const currentBands = computed(() => {
      const today = new Date().toISOString().slice(0, 10);
      const byKey = new Map();

      for (const b of bands.value) {
        if (b.valid_from && b.valid_from > today) continue;      // non ancora attiva
        if (b.valid_to && b.valid_to < today) continue;          // già chiusa

        const key = [
          b.service,
          b.is_partner_share ? 'share' : 'customer',
          b.operators ?? 'any',
          // La variante fa parte della chiave: la fascia «targa prova» e
          // quella normale hanno lo stesso range e non sono doppioni.
          variante(b),
          b.min_km,
          b.max_km ?? '∞',
        ].join('|');

        const existing = byKey.get(key);
        if (!existing || vinceSu(b, existing)) byKey.set(key, b);
      }
      return [...byKey.values()];
    });

    // Raggruppa le fasce per servizio e, dove il servizio distingue 1 e 2
    // operatori, trasforma gli operatori in colonne.
    //
    // `share` sceglie QUALE dei due listini paralleli: `false` = tariffe Simba,
    // `true` = tariffe che il GESTIONALE PARTNER mostra al proprio cliente in
    // fase di preventivo. Il partner parte
    // dalla tariffa Simba e può alzarla: la differenza è il suo ricarico.
    // Si impaginano allo stesso modo ma restano separati, altrimenti ogni
    // fascia comparirebbe due volte con lo stesso range.
    // `global`: le fasce mostrate sono tutte del listino globale (il partner non
    // ne ha di sue). `hasOwn`: il partner ha fasce sue per quel servizio, in
    // uno qualunque dei due listini: è quello che «Elimina listino» chiude.
    const ownership = (service, list, vari = 'normale') => ({
      global: !list.some(ownBand),
      hasOwn: currentBands.value.some(
        (b) => b.service === service && variante(b) === vari && ownBand(b),
      ),
    });

    const groupBands = (share) => {
      // Servizio + variante: gli spostamenti con targa prova hanno un listino
      // loro, e mescolarlo a quello normale darebbe due prezzi sulla stessa
      // riga di fascia.
      const byService = new Map();
      for (const b of currentBands.value) {
        if (!!b.is_partner_share !== share) continue;
        const chiave = `${b.service}|${variante(b)}`;
        if (!byService.has(chiave)) byService.set(chiave, []);
        byService.get(chiave).push(b);
      }

      const groups = [];
      for (const [chiave, list] of byService) {
        const [service, vari] = chiave.split('|');
        const base = SERVICES[service] || { label: service, tone: 'fleet' };
        const meta = vari === 'prova'
          ? { ...base, label: base.label + ETICHETTA_PROVA }
          : base;
        const twoOperators = list.some((b) => Number(b.operators) === 2);

        if (twoOperators) {
          // una riga per fascia km, con le due colonne operatori
          const byRange = new Map();
          for (const b of list) {
            const key = `${b.min_km}-${b.max_km ?? '\u221e'}`;
            if (!byRange.has(key)) {
              byRange.set(key, {
                key,
                range: kmRange(b.min_km, b.max_km),
                min: Number(b.min_km),
                op1: '\u2014',
                op2: '\u2014',
              });
            }
            const row = byRange.get(key);
            if (Number(b.operators) === 2) row.op2 = priceOf(b);
            else row.op1 = priceOf(b);
          }
          groups.push({
            service, label: meta.label, tone: meta.tone, twoOperators: true,
            immatricolato: vari === 'prova' ? false : null,
            ...ownership(service, list, vari),
            rows: [...byRange.values()].sort((a, z) => a.min - z.min),
          });
        } else {
          groups.push({
            service, label: meta.label, tone: meta.tone, twoOperators: false,
            immatricolato: vari === 'prova' ? false : null,
            ...ownership(service, list, vari),
            rows: list
              .map((b) => ({
                key: b.id,
                range: kmRange(b.min_km, b.max_km),
                price: priceOf(b),
                min: Number(b.min_km),
              }))
              .sort((a, z) => a.min - z.min),
          });
        }
      }
      return groups;
    };

    // ---------------------------- editor delle fasce ----------------------
    // Le fasce NON si modificano in place: si CHIUDE quella in corso
    // (`valid_to` a ieri) e se ne crea una nuova valida da oggi, così i
    // movimenti già confermati mantengono il prezzo di allora. Vedi lo
    // snapshot su `route.applied_price`.
    const bandDialog = ref(false);
    const bandService = ref('');
    const bandLabel = ref('');
    const bandTwoOp = ref(false);
    // `false` = si sta scrivendo il listino dei veicoli NON immatricolati;
    // `null` = quello normale, che vale in ogni caso.
    const bandImmatricolato = ref(null);
    const bandRows = ref([]);
    // Modo della prima fascia di un listino nuovo: quello del servizio.
    const bandDefaultMode = computed(() => defaultModeFor(bandService.value));
    const savingBands = ref(false);

    // Le righe grezze del servizio tornano nella forma della BandTable:
    // una riga per fascia km, con le colonne che servono a quel servizio.
    const rowsForService = (service, vari = 'normale') => {
      const byRange = new Map();
      for (const b of currentBands.value) {
        if (b.service !== service) continue;
        if (variante(b) !== vari) continue;
        const key = `${b.min_km}-${b.max_km ?? '∞'}`;
        if (!byRange.has(key)) {
          byRange.set(key, {
            min_km: Number(b.min_km),
            max_km: b.max_km === null ? null : Number(b.max_km),
            mode: null,
            price: null, price_per_km: null,
            price2: null, price2_per_km: null,
            price_share: null, price_share_per_km: null,
            price_share2: null, price_share2_per_km: null,
          });
        }
        const row = byRange.get(key);
        // Ogni fascia ha il SUO modo. Su una fascia al km
        // l'importo È `price_per_km`: leggerlo da `flat_price` (che vale 0)
        // mostrerebbe la tariffa azzerata e la sposterebbe nel campo del €/km
        // oltre la soglia, corrompendola al salvataggio successivo.
        const mode = b.pricing_mode || 'per_km';
        const value = Number((mode === 'per_km' ? b.price_per_km : b.flat_price) ?? 0);
        // Il €/km oltre la soglia è di ogni riga: leggerlo solo da
        // quella a 1 operatore lo riscriveva anche sui 2 operatori e sulla quota.
        const perKm = isExcessMode(mode) ? Number(b.price_per_km) : null;
        // La quota a 2 operatori ha la sua colonna, separata da quella a 1.
        if (b.is_partner_share && Number(b.operators) === 2) {
          row.price_share2 = value;
          row.price_share2_per_km = perKm;
        } else if (b.is_partner_share) {
          row.price_share = value;
          row.price_share_per_km = perKm;
        } else if (Number(b.operators) === 2) {
          row.price2 = value;
          row.price2_per_km = perKm;
        } else {
          row.price = value;
          row.mode = mode;
          row.price_per_km = perKm;
        }
      }
      return [...byRange.values()]
        .map((r) => ({ ...r, mode: r.mode || defaultModeFor(service) }))
        .sort((a, z) => a.min_km - z.min_km);
    };

    const openBandEditor = (g) => {
      bandService.value = g.service;
      bandLabel.value = g.label;
      bandTwoOp.value = !!g.twoOperators;
      bandImmatricolato.value = g.immatricolato === false ? false : null;
      const vari = g.immatricolato === false ? 'prova' : 'normale';
      const esistenti = rowsForService(g.service, vari);
      // Listino del partner ancora da fare: si parte da qualcosa di sensato
      // invece che da una tabella vuota. Vale sia quando non c'è niente sia
      // quando si vedono solo le fasce GLOBALI, perché anche lì il partner un
      // listino suo non ce l'ha. Le fasce che ha già non si toccano.
      const daFare = !esistenti.length || g.global === true;
      let partenza = [];
      if (daFare) {
        // Per la targa prova si parte dal listino NORMALE del partner, che è
        // il punto di partenza vero: si ritoccano le cifre che cambiano. Solo
        // se non ce l'ha si ricade sui predefiniti.
        partenza = vari === 'prova' ? rowsForService(g.service, 'normale') : [];
        if (!partenza.length) partenza = defaultBandsFor(g.service);
      }
      bandRows.value = partenza.length ? partenza : esistenti;
      bandDialog.value = true;
    };

    // Servizi che il partner potrebbe avere ma che non hanno ancora fasce:
    // senza questo non ci sarebbe modo di crearne il primo listino.
    // SIMBA MOBILITY è il partner con cui si preventivano i servizi
    // chiesti senza partner dall'app: le servono i listini di TUTTI i servizi,
    // non solo quelli della sua famiglia. Chi sia lo dice il backend.
    const simbaMobilityId = ref('');
    const isSimbaMobility = computed(
      () => !!simbaMobilityId.value && simbaMobilityId.value === route.params.id
    );

    const missingServices = computed(() => {
      const have = new Set(currentBands.value.map((b) => b.service));
      const wanted = isSimbaMobility.value
        ? ['pickup', 'pickup_loaner', 'escort', 'journey', 'restaurant', 'restaurant_night']
        : isNotturno.value
        ? ['restaurant', 'restaurant_night']
        : ['pickup', 'pickup_loaner', 'escort', 'journey'];
      const mancanti = wanted
        .filter((sv) => !have.has(sv))
        .map((sv) => ({
          service: sv,
          label: (SERVICES[sv] || { label: sv }).label,
          tone: (SERVICES[sv] || { tone: 'fleet' }).tone,
          twoOperators: sv === 'journey',
          rows: [],
        }));

      // Listino dei veicoli NON immatricolati (targa prova). Si propone solo
      // se il partner fa spostamenti e non ce l'ha ancora. A differenza degli
      // altri la sua assenza non lascia scoperto niente — si tariffa con il
      // listino normale — e l'avviso deve dirlo, altrimenti si legge come un
      // errore quando non lo è.
      const faSpostamenti = have.has('journey') || wanted.includes('journey');
      const haProva = currentBands.value.some(
        (b) => b.service === 'journey' && variante(b) === 'prova',
      );
      if (faSpostamenti && !haProva) {
        mancanti.push({
          service: 'journey',
          immatricolato: false,
          label: (SERVICES.journey || { label: 'journey' }).label + ETICHETTA_PROVA,
          tone: (SERVICES.journey || { tone: 'fleet' }).tone,
          twoOperators: true,
          ripiego: 'Senza questo listino le corse con un veicolo non '
            + 'immatricolato si tariffano con il listino normale: nessun '
            + 'errore, ma nessun prezzo diverso.',
          rows: [],
        });
      }
      return mancanti;
    });

    const saveBands = async () => {
      // Stesse regole del backend, dette prima di chiudere il listino in corso.
      const problem = bandRowsProblem(bandRows.value, bandService.value, {
        twoOperators: bandTwoOp.value,
      });
      if (problem) {
        ElMessage.warning(problem);
        return;
      }
      savingBands.value = true;
      const today = new Date().toISOString().slice(0, 10);
      // chiusura a IERI: una fascia chiusa oggi resterebbe valida per le
      // corse di oggi e si sovrapporrebbe alla nuova.
      const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);

      try {
        // 1) prepara le nuove fasce
        const jobs = [];
        for (const r of bandRows.value) {
          // Il modo di calcolo è quello della RIGA: `bandPricing`
          // lo traduce nei campi del backend, uguale per 1 e 2 operatori e
          // per la quota partner. Il €/km invece è di ogni colonna; se vuoto
          // vale quello della prima.
          const svc = bandService.value;
          const mode = r.mode || defaultModeFor(svc);
          const base = {
            partner: { id: route.params.id },
            service: svc,
            min_km: r.min_km,
            max_km: r.max_km,
            valid_from: today,
            // `false` = fascia dei veicoli NON immatricolati; `null` = fascia
            // che vale in ogni caso. È la variante che si sta scrivendo.
            immatricolato: bandImmatricolato.value,
          };
          jobs.push({ ...base, operators: bandTwoOp.value ? 1 : null,
            is_partner_share: false, ...bandPricing(svc, r.price, r.price_per_km, mode) });
          if (bandTwoOp.value) {
            jobs.push({ ...base, operators: 2, is_partner_share: false,
              ...bandPricing(svc, amountOr(r.price2, r.price),
                amountOr(r.price2_per_km, r.price_per_km), mode) });
          }
          if (r.price_share != null && r.price_share !== '') {
            jobs.push({ ...base, operators: bandTwoOp.value ? 1 : null,
              is_partner_share: true, ...bandPricing(svc, r.price_share,
                amountOr(r.price_share_per_km, r.price_per_km), mode) });
          }
          // Quota a 2 operatori: senza questa riga il preventivo del partner
          // per una corsa a 2 operatori non trova la fascia.
          if (bandTwoOp.value && r.price_share2 != null && r.price_share2 !== '') {
            jobs.push({ ...base, operators: 2, is_partner_share: true,
              ...bandPricing(svc, r.price_share2,
                amountOr(r.price_share2_per_km, amountOr(r.price_share_per_km, r.price_per_km)), mode) });
          }
        }
        // 2) salvale, in UNA richiesta. Un listino completo sono ~60 fasce:
        // mandarle una per una voleva dire 60 POST più altrettanti preflight
        // CORS, a sei connessioni per volta, e il salvataggio restava a
        // girare. La `bulk` del CRUD le scrive tutte insieme o nessuna.
        if (jobs.length) await partnerAPI.createBands(jobs);

        // 3) solo ORA chiudi le fasce vecchie di questo servizio.
        //
        // Prima venivano chiuse per prime: se poi la creazione falliva, il
        // partner restava senza listino. In questo ordine un errore lascia
        // tutto com'era, e nell'attimo in cui vecchie e nuove convivono vince
        // comunque la nuova (il backend prende la `valid_from` più recente).
        //
        // ⚠️ Solo le SUE. `currentBands` contiene anche le fasce GLOBALI
        // (partner NULL) quando il partner non ne ha di proprie: chiuderle da
        // qui toglieva il listino di ripiego a TUTTI i partner.
        // Solo le fasce della STESSA variante: scrivendo il listino della
        // targa prova non si chiude quello normale, e viceversa.
        const variDaChiudere = bandImmatricolato.value === false ? 'prova' : 'normale';
        const toClose = currentBands.value.filter(
          (b) => b.service === bandService.value
            && variante(b) === variDaChiudere
            && ownBand(b),
        );
        // A blocchi, non tutte insieme: il browser apre sei connessioni per
        // volta e ogni PATCH si porta dietro il suo preflight.
        const closed = await aBlocchi(toClose, 5, (b) => partnerAPI.closeBand(b.id, yesterday));
        const koClose = closed.filter((r) => r.status === 'rejected').length;

        if (koClose) {
          ElMessage.error(
            `Listino aggiornato, ma ${koClose} fasce vecchie non si sono chiuse: ` +
            'restano a listino con la data di prima. Ricontrollale.'
          );
        } else {
          ElMessage.success('Listino aggiornato.');
        }
        bandDialog.value = false;
        await load();
      } catch (e) {
        console.error('Aggiornamento fasce fallito:', e);
        // Le nuove fasce si salvano prima di chiudere le vecchie: se si arriva
        // qui il listino di prima è ancora intero.
        ElMessage.error(
          'Listino non salvato: resta quello di prima. Controlla la connessione e riprova.'
        );
      } finally {
        savingBands.value = false;
      }
    };

    // ------------------------------------------------------------------
    //  Elimina listino
    //
    //  Le fasce di `tariff_band` hanno le date di validità: «eliminare» un
    //  listino significa CHIUDERE a ieri le fasce del partner, non cancellare
    //  righe. Le corse già confermate hanno il prezzo congelato e non
    //  cambiano; da oggi vale il listino globale, se c'è, altrimenti 0.
    // ------------------------------------------------------------------
    const deleteListino = async (g) => {
      const today = new Date().toISOString().slice(0, 10);
      // Si chiude SOLO la variante di questa card: il listino della targa
      // prova e quello normale si eliminano uno per volta.
      const vari = g.immatricolato === false ? 'prova' : 'normale';
      const own = currentBands.value.filter(
        (b) => b.service === g.service && variante(b) === vari && ownBand(b),
      );
      if (!own.length) return;
      const globalExists = bands.value.some(
        (b) => b.service === g.service && variante(b) === vari && !ownBand(b)
          && !(b.valid_from && b.valid_from > today) && !(b.valid_to && b.valid_to < today),
      );
      try {
        await ElMessageBox.confirm(
          `Le fasce «${g.label}» di questo partner vengono chiuse a ieri. ` +
          (vari === 'prova'
            // Togliere il listino dedicato non lascia scoperto niente: si
            // torna a tariffare con quello normale.
            ? 'Da oggi le corse con targa prova torneranno a usare il listino normale.'
            : globalExists
              ? 'Da oggi le sue corse useranno il listino globale Simba.'
              : 'Finché non ne imposti di nuove, le corse di questo servizio quotano 0.') +
          ' Le corse già confermate non cambiano prezzo.',
          `Elimina listino · ${g.label}`,
          { confirmButtonText: 'Elimina listino', cancelButtonText: 'Annulla', type: 'warning' },
        );
      } catch (e) {
        return; // annullato
      }
      const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
      const res = await aBlocchi(own, 5, (b) => partnerAPI.closeBand(b.id, yesterday));
      const ko = res.filter((r) => r.status === 'rejected').length;
      if (ko) {
        ElMessage.error(`${ko} fasce su ${own.length} non sono state chiuse: ricontrolla il listino.`);
      } else {
        ElMessage.success(`Listino «${g.label}» eliminato.`);
      }
      await load();
    };

    // ------------------------------------------------------------------
    //  Fasce valet
    //
    //  Stessa regola del backend (`ValetService.priceForKm`): vale il listino
    //  del partner se ne ha uno, altrimenti TUTTO quello globale. Mai
    //  mescolati.
    // ------------------------------------------------------------------
    const ownValetBands = computed(() => valetBands.value.filter(ownBand));
    const valetIsGlobal = computed(() => !ownValetBands.value.length && valetBands.value.length > 0);
    const effectiveValetBands = computed(() =>
      (ownValetBands.value.length ? ownValetBands.value : valetBands.value)
        .slice()
        .sort((a, z) => Number(a.from_km) - Number(z.from_km)),
    );

    // Scaglioni di volume del listino: uno, due, tre o più pickup.
    const PICKUP_OPTIONS = [1, 2, 3];
    const monthlyOf = (v) => Math.round((Number(v) / 12) * 100) / 100;
    const numOrNull = (v) => (v === null || v === undefined || v === '' ? null : Number(v));

    const valetDialog = ref(false);
    const valetRows = ref([]);
    const savingValet = ref(false);
    const valetError = ref('');

    const openValetEditor = () => {
      const own = ownValetBands.value.length > 0;
      // Senza listino proprio si parte da quello globale come traccia: gli id
      // si tolgono, così salvando nascono fasce del partner e il globale resta.
      valetRows.value = effectiveValetBands.value.map((b) => ({
        id: own ? b.id : undefined,
        min_km: Number(b.from_km),
        max_km: b.to_km === null ? null : Number(b.to_km),
        p1: numOrNull(b.annual_price_1),
        p2: numOrNull(b.annual_price_2),
        p3: numOrNull(b.annual_price_3),
        km: numOrNull(b.excess_price_per_km),
        // i prezzi del venditore esistono solo sulle fasce del partner
        pp1: own ? numOrNull(b.partner_annual_price_1) : null,
        pp2: own ? numOrNull(b.partner_annual_price_2) : null,
        pp3: own ? numOrNull(b.partner_annual_price_3) : null,
        pkm: own ? numOrNull(b.partner_excess_price_per_km) : null,
      }));
      valetError.value = '';
      valetDialog.value = true;
    };

    const isBlank = (v) => v === '' || v === null || v === undefined || Number.isNaN(Number(v));

    // Il backend prende la PRIMA fascia che contiene i km (confini inclusi):
    // due fasce sovrapposte non danno errore, danno un prezzo a caso.
    const valetRowsProblem = (rows) => {
      const sorted = [...rows].sort((a, z) => Number(a.min_km) - Number(z.min_km));
      for (let i = 0; i < sorted.length; i++) {
        const r = sorted[i];
        if (isBlank(r.min_km) || Number(r.min_km) < 0) return 'Ogni fascia deve avere il km di partenza.';
        // Niente fasce senza limite nel listino valet: i km oltre il pacchetto
        // si pagano al €/km. Una fascia aperta del passato va chiusa qui.
        if (isBlank(r.max_km)) {
          return `La fascia da ${r.min_km} km non ha il km finale: le fasce valet non possono essere senza limite.`;
        }
        if (Number(r.max_km) <= Number(r.min_km)) {
          return `La fascia da ${r.min_km} km deve finire oltre il suo inizio.`;
        }
        for (const n of PICKUP_OPTIONS) {
          if (isBlank(r[`p${n}`]) || Number(r[`p${n}`]) < 0) {
            return `Fascia da ${r.min_km} km: manca il prezzo per ${n === 3 ? '3 o più' : n} pickup.`;
          }
        }
        // il €/km dei km in più è facoltativo, ma non negativo
        if (!isBlank(r.km) && Number(r.km) < 0) {
          return `Fascia da ${r.min_km} km: il prezzo al km non può essere negativo.`;
        }
        const next = sorted[i + 1];
        if (next && Number(next.min_km) < Number(r.max_km)) {
          return `Le fasce da ${r.min_km} e da ${next.min_km} km si sovrappongono.`;
        }
      }
      return '';
    };

    const saveValetBands = async () => {
      const problem = valetRowsProblem(valetRows.value);
      if (problem) {
        valetError.value = problem;
        return;
      }
      valetError.value = '';
      savingValet.value = true;
      try {
        const partnerId = route.params.id;
        const byId = new Map(ownValetBands.value.map((b) => [b.id, b]));
        const kept = new Set(valetRows.value.map((r) => r.id).filter(Boolean));

        const writes = [];
        for (const r of valetRows.value) {
          // Solo il listino Simba: i prezzi del venditore li scrive il partner.
          const fields = {
            from_km: Number(r.min_km),
            to_km: Number(r.max_km),
            annual_price_1: Number(r.p1),
            annual_price_2: Number(r.p2),
            annual_price_3: Number(r.p3),
            // facoltativo: senza, i km oltre il pacchetto non si addebitano
            excess_price_per_km: numOrNull(r.km),
          };
          const old = r.id ? byId.get(r.id) : null;
          if (!old) {
            writes.push(partnerAPI.createValetBand({ partner: { id: partnerId }, ...fields }));
          } else if (Object.keys(fields).some((k) => numOrNull(old[k]) !== fields[k])) {
            writes.push(partnerAPI.updateValetBand(r.id, fields));
          }
        }
        const koWrites = (await Promise.allSettled(writes)).filter((x) => x.status === 'rejected').length;

        // Le eliminazioni per ultime. Una fascia già usata da un addebito o
        // venduta in un pacchetto è legata alla prenotazione o al pacchetto
        // (FK senza cascade) e il server la rifiuta.
        const removed = ownValetBands.value.filter((b) => !kept.has(b.id));
        const koDeletes = (await Promise.allSettled(removed.map((b) => partnerAPI.deleteValetBand(b.id))))
          .filter((x) => x.status === 'rejected').length;

        if (koWrites) {
          ElMessage.error(`${koWrites === 1 ? 'Una modifica non è stata salvata' : `${koWrites} modifiche non sono state salvate`}: ricontrolla il listino valet.`);
        } else if (koDeletes) {
          ElMessage.warning({
            message: `${koDeletes === 1 ? 'Una fascia è già usata' : `${koDeletes} fasce sono già usate`} da un addebito o da un pacchetto venduto e non si può togliere: resta nel listino.`,
            duration: 8000,
            showClose: true,
          });
        } else {
          ElMessage.success('Fasce valet aggiornate.');
        }
        valetDialog.value = false;
        await load();
      } catch (e) {
        console.error('Aggiornamento fasce valet fallito:', e);
        ElMessage.error('Aggiornamento non riuscito');
      } finally {
        savingValet.value = false;
      }
    };

    const tariffGroups = computed(() => groupBands(false));
    const partnerGroups = computed(() => groupBands(true));

    // Il prezzo si legge in modo diverso a seconda del modo della fascia.
    function priceOf(b) {
      const mode = b.pricing_mode || 'per_km';
      const perKm = `${Number(b.price_per_km || 0).toFixed(2).replace('.', ',')} €/km`;
      if (mode === 'per_km') return perKm;
      const base = euro(b.flat_price ?? 0);
      if (!Number(b.price_per_km)) return base;
      if (mode === 'flat_plus_km_total') return `${base} + ${perKm} su tutti i km`;
      return `${base} + ${perKm} oltre ${Number(b.min_km).toLocaleString('it-IT')} km`;
    }

    // Driver operativi per PROVINCIA (è così che il planner li cerca). Le sedi
    // di un partner stanno spesso nella stessa provincia: si conta una volta
    // per provincia, non una per sede.
    // Un conteggio mancante resta `null` ("in corso"), non 0: uno zero
    // inventato qui direbbe "sede senza copertura", che è tutt'altro.
    const driversByProvince = ref({});
    const driverCount = (h) =>
      (h?.provinceId ? driversByProvince.value[h.provinceId] ?? null : null);
    const provinceLabel = (h) => h?.province_ref?.name || h?.province || '—';

    const loadDriverCoverage = async () => {
      const provinceIds = [...new Set(
        headquarters.value.map((h) => h.provinceId).filter(Boolean),
      )];
      await Promise.all(provinceIds.map(async (provinceId) => {
        try {
          driversByProvince.value = {
            ...driversByProvince.value,
            [provinceId]: await driverAPI.countDriversInProvince(provinceId),
          };
        } catch (e) {
          console.warn('Copertura driver non disponibile per la provincia:', e?.message);
        }
      }));
    };

    const load = async () => {
      loading.value = true;
      const id = route.params.id;
      try {
        const res = await partnerAPI.getPartnerDetail(id);
        partner.value = res.data ?? {};
        headquarters.value = partner.value.headquarters || [];
        loadDriverCoverage();
      } catch (e) {
        console.error('Caricamento partner fallito:', e);
        ElMessage.error('Impossibile caricare il partner');
      } finally {
        loading.value = false;
      }

      // Sorgenti indipendenti: se una non risponde, il resto della pagina
      // deve restare utilizzabile.
      const [pay, api, emp, bnd, vbd, adv] = await Promise.allSettled([
        partnerAPI.getPaymentInfo(id),
        partnerAPI.getApiAccessStatus(id),
        partnerAPI.getEmployeesByPartner(id),
        partnerAPI.getBandsByPartner(id),
        partnerAPI.getValetBandsByPartner(id),
        partnerAPI.getAdvisorsByPartner(id),
      ]);
      if (pay.status === 'fulfilled') payment.value = pay.value || {};
      if (api.status === 'fulfilled') apiAccess.value = api.value || { enabled: false };
      if (emp.status === 'fulfilled') employees.value = emp.value || [];
      if (bnd.status === 'fulfilled') bands.value = bnd.value || [];
      if (vbd.status === 'fulfilled') valetBands.value = vbd.value || [];
      if (adv.status === 'fulfilled') advisors.value = adv.value || [];

      // Se questo è il partner-jolly, l'editor gli propone tutti i
      // listini. Best-effort: senza risposta la pagina resta com'era.
      try {
        const simba = await partnerAPI.getSimbaMobility();
        simbaMobilityId.value = simba?.id || '';
      } catch (e) {
        console.warn('Partner-jolly non risolto:', e?.message);
      }

      resetDraft();
    };

    // Il draft va allineato anche fuori dalla modifica: i campi del riepilogo
    // leggono da qui quando si entra in edit.
    const resetDraft = () => {
      Object.assign(draft, {
        company_name: partner.value.company_name,
        piva: partner.value.piva,
        mail: partner.value.mail,
        phone_number: partner.value.phone_number,
        provvigione: partner.value.provvigione,
        // I servizi sono due colonne del partner: `booking_type` accende il
        // pickup, `journey_type` gli spostamenti interni. Per un notturno il
        // primo vale sempre e non si tocca (vedi `save`).
        booking_type: !!partner.value.booking_type,
        journey_type: !!partner.value.journey_type,
        // le Nexi arrivano dall'endpoint dedicato, non dall'entità
        API_key: payment.value.API_key,
        domain: payment.value.domain,
        alias: payment.value.alias,
      });
    };

    const startEdit = () => { resetDraft(); editing.value = true; };
    const cancelEdit = () => { resetDraft(); editing.value = false; };

    const save = async () => {
      saving.value = true;
      // ⚠️ LA CHIAVE NEXI NON SI SALVA DA QUI, e il PATCH non lo dice: risponde
      // 200 come se fosse andato. Sull'entità `API_key` è `@Exclude()` senza
      // opzioni, quindi l'esclusione vale anche in SCRITTURA, e il salvataggio
      // di @nestjsx/crud passa da `plainToClass` prima del save: il campo
      // sparisce lì. `domain` e `alias` non sono esclusi e si salvano.
      // Finché il backend non la rende scrivibile (`@Exclude({ toPlainOnly:
      // true })` o un endpoint dedicato), qui si verifica il risultato invece
      // di dire «fatto».
      const chiaveVoluta = String(draft.API_key ?? '').trim();
      const chiavePrima = String(payment.value.API_key ?? '').trim();
      // Un notturno vende solo il suo servizio: `booking_type` resta acceso e
      // `journey_type` spento, qualunque cosa ci sia nel draft.
      const servizi = isNotturno.value
        ? { booking_type: true, journey_type: false }
        : { booking_type: !!draft.booking_type, journey_type: !!draft.journey_type };
      try {
        await partnerAPI.updatePartner({ id: route.params.id, ...draft, ...servizi });
        editing.value = false;
        await load();
        const chiaveDopo = String(payment.value.API_key ?? '').trim();
        if (chiaveVoluta !== chiavePrima && chiaveDopo !== chiaveVoluta) {
          ElMessage({
            type: 'warning',
            message: 'Partner aggiornato, ma la chiave Nexi NON è stata salvata: '
              + 'il server la scarta. Dominio e alias sì. Va abilitata la scrittura lato backend.',
            duration: 0,
            showClose: true,
          });
        } else {
          ElMessage.success('Partner aggiornato');
        }
      } catch (e) {
        console.error('Salvataggio partner fallito:', e);
        ElMessage.error(
          e?.response?.status === 403
            ? 'Non hai i permessi per modificare questo partner'
            : 'Salvataggio non riuscito'
        );
      } finally {
        saving.value = false;
      }
    };

    const issueKey = async () => {
      apiBusy.value = true;
      try {
        const res = await partnerAPI.issueApiKey(route.params.id);
        freshKey.value = res?.api_key || '';
        apiAccess.value = await partnerAPI.getApiAccessStatus(route.params.id);
      } catch (e) {
        console.error('Emissione chiave fallita:', e);
        ElMessage.error('Emissione della chiave non riuscita');
      } finally {
        apiBusy.value = false;
      }
    };

    const revokeKey = async () => {
      // Le integrazioni che usano la chiave smettono subito di funzionare.
      try {
        await ElMessageBox.confirm(
          "Le integrazioni del partner che usano questa chiave smettono subito di funzionare.",
          'Revocare la chiave API?',
          { confirmButtonText: 'Revoca', cancelButtonText: 'Annulla', type: 'warning' }
        );
      } catch {
        return;
      }
      apiBusy.value = true;
      try {
        await partnerAPI.revokeApiKey(route.params.id);
        freshKey.value = '';
        apiAccess.value = await partnerAPI.getApiAccessStatus(route.params.id);
        ElMessage.success('Chiave revocata');
      } catch (e) {
        console.error('Revoca chiave fallita:', e);
        ElMessage.error('Revoca non riuscita');
      } finally {
        apiBusy.value = false;
      }
    };

    const copyKey = async () => {
      try {
        await navigator.clipboard.writeText(freshKey.value);
        ElMessage.success('Chiave copiata');
      } catch {
        ElMessage.warning('Copia non riuscita: selezionala a mano');
      }
    };

    const addressOf = (h) =>
      [
        [h.address, h.street_number].filter(Boolean).join(' '),
        [h.cap, h.city].filter(Boolean).join(' '),
      ].filter(Boolean).join(', ') || '—';

    // `latitude`/`longitude` hanno default 0: zero significa "mai geolocalizzata".
    const hasCoords = (h) => !!(Number(h.latitude) && Number(h.longitude));

    const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('it-IT') : '—');

    const empInitials = (e) =>
      `${(e?.name || '')[0] || ''}${(e?.surname || '')[0] || ''}`.toUpperCase() || '?';

    const goBack = () => router.push('/partner');
    const openEmployee = (e) => { if (e?.id) router.push(`/employee/${e.id}`); };

    // ------------------------------------------------------------------
    //  Nuova sede / modifica sede: il form è in HeadquarterDialog, condiviso
    //  con il dettaglio della richiesta di prenotazione.
    // ------------------------------------------------------------------
    const hqDialog = ref(false);
    const hqEditing = ref(null);

    const addHeadquarter = () => {
      hqEditing.value = null;
      hqDialog.value = true;
    };

    const openHeadquarter = (h) => {
      if (!canEdit.value || !h?.id) return;
      hqEditing.value = h;
      hqDialog.value = true;
    };

    // ------------------------------------------------------------------
    //  Nuovo dipendente, in un dialogo dove partner e sedi sono già caricati.
    //
    //  ⚠️ Il dipendente si crea con POST /auth/register e va legato al
    //  PARTNER, non solo alle sedi: `employee.partnerId` è la regola di
    //  dominio dell'entità ("1 account = 1 partner"). Senza, e senza sedi, il
    //  dipendente viene creato davvero ma resta orfano — l'elenco qui sotto
    //  passa da `headquarters.partner.id` e non lo mostrerebbe mai.
    // ------------------------------------------------------------------
    const empDialog = ref(false);
    const savingEmp = ref(false);
    const empDraft = ref({
      name: '', surname: '', mail: '', telephone_number: '',
      role: 'employee', headquarterIds: [],
    });
    const EMP_ROLES = [
      { value: 'employee', label: 'Dipendente' },
      { value: 'salesvendor', label: 'Venditore' },
      { value: 'admin', label: 'Amministratore' },
    ];

    // ------------------------------------------------------------------
    //  Rinvia accesso
    //
    //  Capita che la mail di benvenuto non arrivi (un bounce del provider, un
    //  filtro antispam). Si rimanda la STESSA mail, con utente e password. La
    //  password di allora non è recuperabile (a DB c'è solo il suo hash): il
    //  server ne genera una nuova, e quella vecchia smette di valere. Se il
    //  servizio di posta rifiuta l'invio resta quella di prima.
    //
    //  La password nuova si mostra anche qui: un bounce arriva dopo, e allora
    //  la si può comunicare per un'altra strada.
    // ------------------------------------------------------------------
    // ---- elimina dipendente -----------------------------------------------
    // Non si cancella la riga: il dipendente non entra più, la sua email si
    // libera e resta nello storico (vedi EmployeeService.elimina).
    const eliminandoDip = ref('');
    const eliminaDipendente = async (e) => {
      const nome = [e.name, e.surname].filter(Boolean).join(' ') || e.mail || 'il dipendente';
      try {
        await ElMessageBox.confirm(
          `${nome} non potrà più entrare nel gestionale e la sua email si potrà usare per un nuovo dipendente. ` +
          'Resta nello storico delle prenotazioni e degli spostamenti che ha gestito.',
          `Eliminare ${nome}?`,
          { confirmButtonText: 'Elimina', cancelButtonText: 'Annulla', type: 'warning' },
        );
      } catch (annullato) {
        return;
      }
      eliminandoDip.value = e.id;
      try {
        await partnerAPI.deleteEmployee(e.id);
        employees.value = employees.value.filter((x) => x.id !== e.id);
        ElMessage.success(`${nome} eliminato`);
      } catch (err) {
        // il messaggio del server lo mostra l'interceptor
        console.error('Eliminazione del dipendente fallita:', err?.response?.status);
      } finally {
        eliminandoDip.value = '';
      }
    };

    const inviandoAccesso = ref('');
    const credenziali = ref(null);

    const inviaAccesso = async (e) => {
      if (!e?.mail) return;
      try {
        await ElMessageBox.confirm(
          `A ${e.mail} arriva di nuovo la mail di benvenuto con utente e password. ` +
          'La password è NUOVA: quella che ha adesso smette di funzionare.',
          'Rinvia accesso',
          { confirmButtonText: 'Invia', cancelButtonText: 'Annulla', type: 'warning' },
        );
      } catch (annullato) {
        return;
      }
      inviandoAccesso.value = e.id;
      try {
        const res = await authAPI.resendCredentials(e.id);
        credenziali.value = { mail: res.data?.mail || e.mail, password: res.data?.password || '' };
      } catch (err) {
        // il messaggio del server (es. posta rifiutata) lo mostra l'interceptor
        console.error('Rinvio delle credenziali fallito:', err?.response?.status);
      } finally {
        inviandoAccesso.value = '';
      }
    };

    const copiaCredenziali = async () => {
      const c = credenziali.value;
      if (!c) return;
      try {
        await navigator.clipboard.writeText(`Utente: ${c.mail}\nPassword: ${c.password}`);
        ElMessage.success('Credenziali copiate.');
      } catch {
        ElMessage.warning('Copia non riuscita: selezionale a mano.');
      }
    };

    const addEmployee = () => {
      empDraft.value = {
        name: '', surname: '', mail: '', telephone_number: '',
        role: 'employee',
        // per difetto tutte le sedi: un dipendente senza sede non è
        // rappresentabile, ed è il caso che lo rendeva invisibile
        headquarterIds: headquarters.value.map((h) => h.id),
      };
      empDialog.value = true;
    };

    const canSaveEmp = computed(() => {
      const d = empDraft.value;
      return !!d.name.trim() && !!d.surname.trim() && !!d.mail.trim();
    });

    const saveEmployee = async () => {
      savingEmp.value = true;
      const d = empDraft.value;
      try {
        await partnerAPI.addEmployee({
          name: d.name.trim(),
          surname: d.surname.trim(),
          mail: d.mail.trim(),
          // NOT NULL a schema senza default: omesso darebbe 500
          telephone_number: d.telephone_number.trim(),
          role: d.role,
          partner: { id: route.params.id },
          headquarters: d.headquarterIds.map((id) => ({ id })),
        });
        ElMessage.success('Dipendente creato. Riceverà le credenziali via email.');
        empDialog.value = false;
        await load();
      } catch (e) {
        console.error('Creazione dipendente fallita:', e);
        ElMessage.error(
          e?.response?.status === 409
            ? 'Esiste già un utente con questa email.'
            : 'Creazione non riuscita',
        );
      } finally {
        savingEmp.value = false;
      }
    };

    onMounted(load);
    // Da un partner a un altro (link interni, avanti/indietro) il componente
    // resta lo stesso: senza questo la pagina mostrerebbe il partner di prima.
    // ⚠️ Solo restando su un PARTNER: andando su un'altra pagina con un id
    // (es. /employee/:id) la pagina che esce vede cambiare l'id e caricava il
    // partner con l'id del dipendente («Impossibile caricare il partner»).
    watch(() => route.params.id, (id, prev) => {
      if (!id || id === prev || !route.path.startsWith('/partner/')) return;
      editing.value = false;
      load();
    });

    return {
      reportMonth, reportBusy, downloadReport,
      partner, payment, apiAccess, headquarters, employees, valetBands,
      loading, editing, saving, apiBusy, freshKey, draft,
      canEdit, isNotturno, maskedNexiKey, advisorNames, activeServices,
      sediSummary, tariffGroups, partnerGroups,
      driverCount,
      provinceLabel,
      bandDialog, bandService, bandLabel, bandTwoOp, bandRows, savingBands,
      openBandEditor, saveBands, missingServices, deleteListino, isSimbaMobility,
      ownValetBands, valetIsGlobal, effectiveValetBands,
      valetDialog, valetRows, savingValet, valetError, openValetEditor, saveValetBands,
      PICKUP_OPTIONS, monthlyOf,
      unitFor, unitForMode, bandDefaultMode,
      startEdit, cancelEdit, save, issueKey, revokeKey, copyKey,
      addressOf, hasCoords, fmtDate, euro, kmRange, empInitials,
      goBack, openHeadquarter, openEmployee, addHeadquarter, addEmployee,
      inviaAccesso, inviandoAccesso, credenziali, copiaCredenziali, eliminaDipendente, eliminandoDip,
      empDialog, empDraft, savingEmp, EMP_ROLES, canSaveEmp, saveEmployee,
      empCerca, empRuolo, empSede, ruoliPresenti, empFiltrati,
      hqDialog, hqEditing, load,
      roleLabel: (r) => ROLE_LABELS[r] || r || '—',
    };
  },
};
</script>

<style scoped>
/* ---- report mensile ---- */
.report { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.report__month { flex: 1; min-width: 150px; }
.report__hint { margin: 8px 0 0; font-size: 12px; line-height: 1.5; color: var(--text-tertiary); }

.pdt { padding: 4px 0 32px; }
.pdt__sp { flex: 1; }
.cred__txt { margin: 0 0 14px; font-size: 13px; line-height: 1.5; color: var(--text-secondary); word-break: normal; }
.cred__box {
  display: grid; grid-template-columns: auto 1fr; gap: 8px 14px; align-items: center;
  padding: 12px 14px; border: 1px solid var(--border-color); border-radius: 8px;
}
.cred__lbl {
  font-size: 10.5px; font-weight: 700; letter-spacing: 0.05em;
  text-transform: uppercase; color: var(--text-tertiary);
}
.cred__val { font-size: 14px; color: var(--text-primary); user-select: all; word-break: break-all; }
.pdt__dash { color: var(--text-tertiary); }

.sede__cov {
  margin-top: 3px; font-size: 11.5px; color: var(--text-secondary);
}
.sede__cov.is-missing { color: var(--state-attn); }

/* ------------------------------- intestazione ---------------------------- */
.pdt__head {
  display: flex; align-items: center; gap: 14px; flex-wrap: wrap; margin: 0 2px 16px;
}
.pdt__back {
  width: 36px; height: 36px; flex: none; border-radius: 9px;
  border: 1px solid var(--border-color); background: var(--bg-primary);
  display: grid; place-items: center; cursor: pointer;
  font-size: 16px; color: var(--text-secondary); font-family: inherit;
}
.pdt__back:hover { border-color: var(--brand-violet); }
.pdt__htitle h1 {
  margin: 0; font-size: 21px; display: flex; align-items: center;
  gap: 10px; flex-wrap: wrap; color: var(--text-primary);
}
.pdt__idmono {
  margin-top: 3px; font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12.5px; color: var(--text-tertiary);
}
.pdt__acts { display: flex; gap: 8px; flex-wrap: wrap; }

/* --------------------------------- pillole ------------------------------- */
.tpill {
  display: inline-flex; align-items: center; gap: 6px; white-space: nowrap;
  font-size: 12px; font-weight: 650; padding: 3px 10px; border-radius: 20px;
  border: 1px solid transparent;
}
.tpill::before {
  content: ""; width: 6px; height: 6px; border-radius: 50%; background: currentColor;
}
.tpill--auto { color: #2e5aac; background: #e4ecfa; border-color: #c3d4f0; }
.tpill--nott {
  color: var(--svc-notturno); background: var(--svc-notturno-bg); border-color: #cdcfe6;
}

.chip {
  display: inline-flex; align-items: center; gap: 5px; width: max-content;
  font-size: 11.5px; font-weight: 650; padding: 2px 9px; border-radius: 20px;
}
.chip::before {
  content: ""; width: 6px; height: 6px; border-radius: 50%; background: currentColor;
}
.chip--pickup { color: var(--svc-pickup-ink); background: var(--svc-pickup-bg); }
.chip--fleet { color: var(--svc-fleet); background: var(--svc-fleet-bg); }
.chip--acc { color: var(--svc-transfer); background: var(--svc-transfer-bg); }
.chip--nott { color: var(--svc-notturno); background: var(--svc-notturno-bg); }

/* --------------------------------- griglia ------------------------------- */
.pdt__grid {
  display: grid; grid-template-columns: 1fr 340px; gap: 16px; align-items: start;
}
@media (max-width: 960px) { .pdt__grid { grid-template-columns: 1fr; } }
.pdt__col { display: flex; flex-direction: column; gap: 16px; }
.pdt__aside {
  position: sticky; top: 16px; display: flex; flex-direction: column; gap: 16px;
}
@media (max-width: 960px) { .pdt__aside { position: static; } }

.card {
  background: var(--bg-primary); border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
}
.card__hd {
  display: flex; align-items: center; gap: 10px;
  padding: 13px 16px; border-bottom: 1px solid var(--border-color);
}
.card__hd h3 { margin: 0; font-size: 14px; color: var(--text-primary); }
.card__bd { padding: 16px; }
/* le sedi scorrono dentro un'altezza fissa invece di allungare la pagina */
.card__bd--scroll { max-height: 320px; overflow-y: auto; }

/* Riquadro-nota al posto di el-alert, che con icona + descrizione impila il
   testo sotto l'icona invece di affiancarlo. */
.warnbox {
  display: flex; align-items: flex-start; gap: 10px;
  padding: 10px 13px; margin-bottom: 14px;
  border-radius: 9px; font-size: 12.5px; line-height: 1.5;
  color: var(--state-attn); background: var(--state-attn-bg);
  border: 1px solid var(--state-attn-line);
}
.warnbox--info {
  color: var(--state-active); background: var(--state-active-bg);
  border-color: var(--state-active-line);
}
.warnbox__ico { flex: none; font-size: 14px; line-height: 1.35; }
.warnbox__txt { flex: 1; text-align: left; }
.warnbox code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px; padding: 0 4px; border-radius: 4px;
  background: rgba(0, 0, 0, 0.06);
}

.pdt__empty { font-size: 13px; color: var(--text-tertiary); padding: 4px 0; }

/* ----------------------------------- sedi -------------------------------- */
.sede {
  border: 1px solid var(--border-color); border-radius: 10px;
  padding: 13px 14px; background: var(--bg-secondary); cursor: pointer;
  transition: border-color var(--transition-fast);
}
.sede + .sede { margin-top: 10px; }
.sede:hover { border-color: var(--brand-violet); }
.sede__st { font-weight: 700; margin-bottom: 6px; color: var(--text-primary); }
.sede__addr { font-size: 13px; color: var(--text-primary); }
.sede__geo {
  margin-top: 3px; font-size: 12px; color: var(--state-ok);
  font-variant-numeric: tabular-nums;
}
.sede__geo.is-missing { color: var(--state-attn); }
.sede__meta {
  display: flex; flex-wrap: wrap; gap: 16px; margin-top: 8px;
  font-size: 12.5px; color: var(--text-secondary);
}
.sede__meta b { color: var(--text-primary); font-weight: 650; }

/* --------------------------------- tariffe ------------------------------- */
/* separatore fra i due listini paralleli (Simba / partner) */
.listino {
  display: flex; align-items: baseline; gap: 8px; flex-wrap: wrap;
  margin: 18px 0 10px; padding-bottom: 6px;
  border-bottom: 1px solid var(--border-color);
}
.listino__t {
  font-size: 11.5px; font-weight: 700; letter-spacing: 0.06em;
  text-transform: uppercase; color: var(--text-secondary);
}
.listino__n { font-size: 12px; color: var(--text-tertiary); }

.subcard {
  border: 1px solid var(--border-color); border-radius: 10px;
  padding: 13px 14px; background: var(--bg-secondary);
}
/* il listino del partner si distingue a colpo d'occhio da quello Simba */
.subcard--share { border-left: 3px solid var(--brand-violet); }
/* servizio previsto ma senza listino: si deve notare */
.subcard--missing { border-left: 3px solid var(--state-attn); }
.pdt__form { display: flex; flex-direction: column; gap: 12px; }
.pdt__grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.pdt__fld label {
  display: block; margin-bottom: 4px; font-size: 10.5px; font-weight: 700;
  letter-spacing: 0.05em; text-transform: uppercase; color: var(--text-tertiary);
}
.pdt__opt { text-transform: none; letter-spacing: 0; font-weight: 500; }
.sede--ro { cursor: default; }
.sede--ro:hover { border-color: var(--border-color); }
.pdt__dlgNote {
  margin: 14px 0 0; font-size: 12px; color: var(--text-tertiary); line-height: 1.5;
}
.subcard + .subcard { margin-top: 12px; }
.subcard__sh {
  display: flex; align-items: center; gap: 8px; margin-bottom: 9px; flex-wrap: wrap;
}
.subcard__title { font-weight: 700; font-size: 13px; color: var(--text-primary); }
.subcard__note { color: var(--text-tertiary); font-weight: 500; font-size: 12px; }
.subcard__note--global { color: var(--state-attn); }
.mtab__sub { display: block; font-size: 11px; color: var(--text-tertiary); font-weight: 500; }
.mtab__sub--share { color: var(--brand-violet); }
.pdt__dlgErr { margin: 12px 0 0; font-size: 12.5px; font-weight: 600; color: var(--state-danger); }
/* Nota sul partner-jolly dei preventivi senza partner */
.pdt__hint {
  margin: 0 0 10px;
  padding: 8px 12px;
  border-radius: 10px;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
  font-size: 12.5px;
  color: var(--text-secondary);
}

.mtab { width: 100%; border-collapse: collapse; }
.mtab th {
  text-align: left; font-size: 10.5px; text-transform: uppercase;
  letter-spacing: 0.04em; color: var(--text-tertiary); font-weight: 700;
  padding: 6px 8px; border-bottom: 1px solid var(--border-color);
}
.mtab td {
  padding: 7px 8px; border-bottom: 1px solid var(--border-color);
  font-size: 13px; color: var(--text-primary);
}
.mtab tr:last-child td { border-bottom: 0; }
.mtab th.num, .mtab td.num { text-align: right; font-variant-numeric: tabular-nums; }

.notebox--plain {
  background: var(--bg-tertiary) !important;
  margin-top: 0 !important; margin-bottom: 10px;
}
.notebox {
  font-size: 12px; color: var(--text-secondary); background: var(--svc-notturno-bg);
  border: 1px solid var(--border-color); border-radius: 9px;
  padding: 9px 12px; margin-top: 10px;
}

/* ------------------------------- dipendenti ------------------------------ */
/* Elenco compatto, una riga per dipendente: con molti dipendenti se ne
   vedono tanti senza scorrere. */
.emp {
  display: flex; align-items: center; gap: 12px; cursor: pointer;
  border-bottom: 1px solid var(--border-color); padding: 8px 6px;
  transition: background var(--transition-fast);
}
.emp:last-child { border-bottom: 0; }
.emp__count {
  margin-left: 8px; font-size: 12px; font-weight: 600; font-variant-numeric: tabular-nums;
  color: var(--text-secondary); background: var(--bg-tertiary); border-radius: 4px; padding: 0 7px;
}
.emp__filters { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 12px; }
.emp__search { flex: 1 1 220px; }
.emp__sel { width: 170px; }
.emp:hover { background: var(--bg-secondary); }
.ava {
  width: 34px; height: 34px; flex: none; border-radius: 50%;
  display: grid; place-items: center; font-size: 12px; font-weight: 700;
  color: #fff; background: var(--avatar-bg);
}
.emp__nm { font-weight: 600; color: var(--text-primary); }
.emp__sub { color: var(--text-tertiary); font-size: 12px; }
.emp__right {
  margin-left: auto; display: flex; align-items: center; justify-content: flex-end;
  flex-wrap: wrap; gap: 6px 8px;
}
/* Il pulsante è un'azione di servizio: sta sotto ruolo e sedi, discreto,
   senza rubare la scena al nome del dipendente. */
.emp__send { margin: 0; }
.emp__rl {
  font-size: 10px; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.03em; color: var(--text-secondary);
  background: var(--bg-tertiary); border: 1px solid var(--border-color);
  border-radius: 5px; padding: 1px 7px;
}
.emp__sedes {
  display: flex; gap: 5px; flex-wrap: wrap; justify-content: flex-end; margin-top: 0;
}
.schip {
  font-size: 10.5px; font-weight: 600; color: var(--text-secondary);
  background: var(--bg-tertiary); border: 1px solid var(--border-color);
  border-radius: 20px; padding: 1px 8px;
}

/* -------------------------------- riepilogo ------------------------------ */
.kv { display: flex; flex-direction: column; }
.kv__r {
  display: flex; justify-content: space-between; align-items: center;
  gap: 12px; padding: 9px 0; border-bottom: 1px solid var(--border-color);
}
.kv__r:last-child { border-bottom: 0; }
.kv__k { color: var(--text-tertiary); font-size: 12.5px; flex: none; }
.kv__v { font-weight: 600; text-align: right; color: var(--text-primary); }
.kv__v--mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12.5px; font-weight: 500;
}
.svcwrap { display: flex; flex-wrap: wrap; gap: 6px; justify-content: flex-end; }

.flag-on {
  color: var(--state-attn); background: var(--state-attn-bg);
  border: 1px solid var(--state-attn-line);
  font-size: 11.5px; font-weight: 650; padding: 2px 9px; border-radius: 20px;
}
.flag-off {
  color: var(--text-tertiary); background: var(--state-muted-bg);
  border: 1px solid var(--state-muted-line);
  font-size: 11.5px; font-weight: 650; padding: 2px 9px; border-radius: 20px;
}

/* ------------------------------- accesso API ----------------------------- */
.pdt__apiRow { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.pdt__apiMeta { font-size: 12.5px; color: var(--text-tertiary); }
.pdt__apiActions { margin-top: 12px; display: flex; gap: 8px; flex-wrap: wrap; }
.pdt__freshKey { margin-top: 12px; margin-bottom: 0; }
.pdt__keyValue {
  display: block; margin: 6px 0 8px; padding: 7px 9px; word-break: break-all;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12.5px;
  background: var(--bg-tertiary); border-radius: var(--radius-base);
}
</style>
